import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, TrendingDown, ExternalLink, Filter, Target, LogOut, RefreshCw } from "lucide-react";

const curSymbol = (c) => (c === "USD" ? "$" : c === "GBP" ? "£" : c === "EUR" ? "€" : c || "");

export default function Deals() {
    const { logout } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState({ deals: [], count: 0, total_potential_savings: 0 });
    const [loading, setLoading] = useState(true);
    const [minPct, setMinPct] = useState(0);
    const [onlyTargetHit, setOnlyTargetHit] = useState(false);

    const load = async (pct = minPct) => {
        setLoading(true);
        try {
            const { data } = await api.get("/wishlist/deals", { params: { min_pct: pct } });
            setData(data);
        } catch (e) {
            toast.error("Failed to load deals");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(0); }, []);

    const rows = useMemo(() => {
        return onlyTargetHit ? data.deals.filter((d) => d.hit_target) : data.deals;
    }, [data.deals, onlyTargetHit]);

    const applyFilter = (pct) => { setMinPct(pct); load(pct); };

    return (
        <div className="min-h-screen bg-neutral-50">
            <header className="border-b border-neutral-200 bg-white sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => nav("/wishlist")} className="border border-neutral-300 h-10 w-10 flex items-center justify-center hover:border-black" data-testid="back-to-wishlist">
                            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Buy-now queue</div>
                            <h1 className="font-display text-2xl tracking-tight leading-none mt-0.5">Deals</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => load(minPct)} className="border border-neutral-300 h-10 px-3 hover:border-black flex items-center gap-2" title="Refresh" data-testid="refresh-deals">
                            <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
                            <span className="uppercase tracking-widest text-[10px] font-semibold hidden sm:inline">Refresh</span>
                        </button>
                        <button onClick={logout} className="border border-neutral-300 h-10 w-10 flex items-center justify-center hover:border-black" title="Sign out">
                            <LogOut className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 py-6">
                {/* Savings hero */}
                <div className="border border-neutral-200 bg-white p-6 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Potential savings</div>
                        <div className="font-display text-5xl tabular-nums mt-1 leading-none" data-testid="total-saved">
                            ${data.total_potential_savings?.toFixed(2)}
                        </div>
                        <div className="text-sm text-neutral-500 font-body mt-2">
                            across <span className="font-semibold text-black" data-testid="deals-count">{data.count}</span> item{data.count === 1 ? "" : "s"} currently discounted vs when you added them.
                        </div>
                    </div>
                    <div className="border-t sm:border-t-0 sm:border-l border-neutral-200 pt-4 sm:pt-0 sm:pl-4 flex sm:flex-col justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Target hits</div>
                            <div className="font-display text-3xl tabular-nums mt-1 leading-none text-green-700" data-testid="target-hits">
                                {data.deals.filter((d) => d.hit_target).length}
                            </div>
                        </div>
                        <button
                            onClick={() => setOnlyTargetHit((v) => !v)}
                            className={`text-[10px] uppercase tracking-widest border px-3 h-8 flex items-center gap-1 self-end sm:self-start sm:mt-2 ${onlyTargetHit ? "bg-green-700 border-green-700 text-white" : "border-black hover:bg-black hover:text-white"}`}
                            data-testid="toggle-target-hit"
                        >
                            <Target className="h-3 w-3" strokeWidth={1.5} />
                            {onlyTargetHit ? "Showing target hits" : "Filter to target hits"}
                        </button>
                    </div>
                </div>

                {/* Min % filter chips */}
                <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">
                        <Filter className="h-3 w-3" strokeWidth={1.5} /> Min drop
                    </div>
                    {[0, 10, 20, 30, 50].map((pct) => (
                        <button
                            key={pct}
                            onClick={() => applyFilter(pct)}
                            className={`text-[10px] uppercase tracking-widest px-3 h-8 border ${minPct === pct ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
                            data-testid={`filter-${pct}`}
                        >
                            {pct === 0 ? "All drops" : `${pct}%+`}
                        </button>
                    ))}
                </div>

                {loading && <div className="text-sm text-neutral-500 font-body">Loading…</div>}

                {!loading && rows.length === 0 && (
                    <div className="border border-dashed border-neutral-300 p-12 text-center bg-white">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Nothing to buy yet</div>
                        <div className="font-display text-2xl mt-2">No deals match this filter</div>
                        <div className="text-sm text-neutral-500 mt-2 font-body max-w-md mx-auto">
                            {minPct > 0
                                ? `Nothing on the radar has dropped ≥${minPct}% yet. Try a lower threshold or check back after the next 6h price check.`
                                : "Track more items and prices will start dropping. The extension checks every 6h."}
                        </div>
                        <Link to="/wishlist" className="inline-block mt-4 text-[10px] uppercase tracking-widest border border-black px-4 h-9 leading-[2.25rem] hover:bg-black hover:text-white">
                            Back to wishlist
                        </Link>
                    </div>
                )}

                {/* Ranked deal list */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rows.map((d, i) => {
                        const rank = i + 1;
                        return (
                            <div key={d.id} className={`bg-white border ${d.hit_target ? "border-green-700 ring-2 ring-green-200" : "border-neutral-200"} flex`} data-testid={`deal-${d.id}`}>
                                {/* Rank ribbon */}
                                <div className="w-10 bg-black text-white flex flex-col items-center justify-center py-3 shrink-0">
                                    <div className="text-[9px] uppercase tracking-widest opacity-60">rank</div>
                                    <div className="font-display text-2xl tabular-nums leading-none mt-1">#{rank}</div>
                                </div>
                                <a href={d.url} target="_blank" rel="noopener noreferrer" className="w-24 sm:w-28 aspect-[4/5] bg-neutral-50 border-r border-neutral-200 shrink-0 overflow-hidden">
                                    {d.image_url ? (
                                        <img src={d.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-neutral-300 text-3xl font-display">?</div>
                                    )}
                                </a>
                                <div className="flex-1 min-w-0 p-3 flex flex-col justify-between">
                                    <div>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-[9px] uppercase tracking-widest bg-neutral-100 border border-neutral-200 px-1.5 py-0.5">{d.source}</span>
                                            {(d.size || d.color) && (
                                                <span className="text-[9px] uppercase tracking-widest bg-blue-50 border border-blue-200 text-blue-800 px-1.5 py-0.5" data-testid={`variant-${d.id}`}>
                                                    {[d.size && `size ${d.size}`, d.color].filter(Boolean).join(" · ")}
                                                </span>
                                            )}
                                            {d.hit_target && (
                                                <span className="text-[9px] uppercase tracking-widest bg-green-700 text-white px-1.5 py-0.5 font-semibold">🎯 target hit</span>
                                            )}
                                        </div>
                                        <div className="font-body text-sm line-clamp-2 mt-1.5">{d.title}</div>
                                    </div>
                                    <div className="mt-2">
                                        <div className="flex items-end justify-between">
                                            <div>
                                                <div className="font-display text-2xl tabular-nums leading-none">
                                                    {curSymbol(d.currency)}{d.current_price?.toFixed(2)}
                                                </div>
                                                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1 line-through">
                                                    {curSymbol(d.currency)}{d.baseline_price?.toFixed(2)}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="bg-green-700 text-white px-2 py-1 font-semibold text-xs tabular-nums flex items-center gap-1">
                                                    <TrendingDown className="h-3 w-3" strokeWidth={2} />-{d.dropped_pct}%
                                                </div>
                                                <div className="text-[10px] uppercase tracking-widest text-green-700 mt-1 font-semibold">
                                                    save {curSymbol(d.currency)}{d.saved?.toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                        <a href={d.url} target="_blank" rel="noopener noreferrer" className="mt-2.5 w-full bg-black text-white h-9 flex items-center justify-center gap-2 uppercase tracking-widest text-[10px] font-semibold hover:bg-neutral-800" data-testid={`buy-${d.id}`}>
                                            <ExternalLink className="h-3 w-3" strokeWidth={1.5} /> Buy on {d.source}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </main>
        </div>
    );
}
